/* eslint-disable no-useless-escape */
import { flowRight, get, join, keys, reduce, truncate } from "lodash";
import TwitterText from "twitter-text";
import { Platform } from "@linkfluence/models/platform";
import { parse } from "@tootools/emoji";

interface SocialAccount {
  reg: RegExp[];
  name: string;
}

type VideoContentType = "thumbnail" | "embed";
interface SocialVideo {
  reg: RegExp[];
  embed: string;
  thumbnail?: string;
}

type SocialAccounts = Record<string, SocialAccount>;
type SocialVideos = Record<string, SocialVideo>;

export const ACCOUNTS_FETCHER: SocialAccounts = {
  facebook: {
    reg: [
      // Example: http://www.facebook.com/leigth.greimar1
      /^https?:\/\/(?:www\.)?facebook\.com\/([\w-\.]+).*$/i,
    ],
    name: "facebook",
  },
  youtube: {
    reg: [
      // Example: http://www.youtube.com/user/UCdn86UYrf54lXfVli9CB6Aw
      /^https?:\/\/(?:www\.)?youtube\.com\/user\/([\w-]+).*$/i,
      // Example: http://www.youtube.com/ytscreeningroom?v=oTJRivZTMLs
      /^https?:\/\/(?:www\.)?youtube\.com\/([\w-]+).*$/i,
    ],
    name: "youtube",
  },
  twitter: {
    reg: [
      // Example: https://twitter.com/BarackObama/
      /^https?:\/\/(?:www\.)?twitter\.com\/([\w-\.]+).*$/i,
    ],
    name: "twitter",
  },
  instagram: {
    reg: [
      // Example: https://www.instagram.com/barackobama/
      /^https?:\/\/(?:www\.)?instagram\.com\/([\w-\.]+).*$/i,
    ],
    name: "instagram",
  },
  linkedin: {
    reg: [
      // Example: http://ca.linkedin.com/in/linkedinyourname
      /^https?:\/\/(?:www\.|[a-z]{2}\.)?linkedin\.com\/in\/([\w-\.]+).*$/i,
    ],
    name: "linkedin",
  },
  pinterest: {
    reg: [
      // Example: http://www.pinterest.com/Padawandu93
      /^https?:\/\/(?:www\.)?pinterest\.com\/([\w-\.]+).*$/i,
      // Example: https://www.pinterest.fr/cmmnteconomiser/
      /^https?:\/\/(?:www\.)?pinterest\.[a-z]{2}\/([\w-\.]+).*$/i,
    ],
    name: "pinterest",
  },
  snapchat: {
    reg: [
      // Example: https://www.snapchat.com/add/selenagomez
      /^https?:\/\/(?:www\.)?snapchat\.com\/add\/([\w-]+).*$/i,
    ],
    name: "snapchat",
  },
  weibo: {
    reg: [
      // Example: http://www.weibo.com/rihannaofficial
      /^https?:\/\/(?:www\.)?weibo\.com\/([\w-\.]+).*$/i,
    ],
    name: "sinaweibo",
  },
  soundcloud: {
    reg: [
      // Example: https://soundcloud.com/justinbieber
      /^https?:\/\/(?:www\.)?soundcloud\.com\/([\w-\.]+).*$/i,
    ],
    name: "soundcloud",
  },
};

const VIDEOS_FETCHER: SocialVideos = {
  all: {
    reg: [/^https?:\/\/(?:www\.)?.*\.mp4.*/i],
    embed: [
      '<video controls="controls" poster="__thumbnail__">',
      '<source src="__url__" type="video/mp4" />',
      "</video>",
    ].join(""),
  },
  facebook: {
    // @see: https://developers.facebook.com/docs/plugins/oembed-endpoints
    // @see: http://stackoverflow.com/questions/29860182/regex-check-facebook-video-url
    reg: [
      /^(https?:\/\/www\.facebook\.com\/(?:video\.php\?(v|id)=\d+|.*?\/videos\/\d+\/?))$/i,
    ],
    embed: '<div class="fb-video" data-href="__url__" data-width="500"></div>',
  },
  youtube: {
    reg: [
      // Example: http://www.youtube.com/watch?v=kUvrUr0DMmo
      /^https?:\/\/(?:www\.)?youtube\.com\/watch\?(?:[^&]*&)*v=([\w-]+).*$/i,
      // Example: http://youtube.googleapis.com/v/kUvrUr0DMmo
      /^https?:\/\/(?:www\.)?youtube\.googleapis\.com\/v\/([\w-]+).*$/i,
      // Example: http://www.youtube.com/v/kUvrUr0DMmo
      /^https?:\/\/(?:www\.)?youtube\.com\/v\/([\w-]+).*$/i,
      // Example: http://www.youtu.be/kUvrUr0DMmo
      /^https?:\/\/(?:www\.)?youtu\.be\/([\w-]+)$/i,
      // Example: https://youtu.be/KDXAkTT6JfM?list=PLifIjYbZ6jFKmMIHBbRe5oqvO1sckYs9O
      /^https?:\/\/(?:www\.)?youtu\.be\/([\w-]+)\?.*$/i,
      // Example: http://www.youtube.com/embed/kUvrUr0DMmo
      /^https?:\/\/(?:www\.)?youtube\.com\/embed\/([\w-]+).*$/i,
      // Example: http://img.youtube.com/vi/I3HdptUXR8w/0.jpg
      /^https?:\/\/(?:www\.)?img\.youtube\.com\/vi\/([\w-]+).*\/.*\.jpg.*$/i,
    ],
    embed:
      '<iframe style="display:block;" class="media youtube" src="https://www.youtube.com/embed/__id__" allowfullscreen></iframe>',
    thumbnail: "https://img.youtube.com/vi/__id__/mqdefault.jpg",
  },
  dailymotion: {
    reg: [
      /^https?:\/\/dailymotion\.com\/video\/([A-Za-z0-9]+).*$/i,
      /^https?:\/\/www\.dailymotion\.com\/video\/([A-Za-z0-9]+).*$/i,
    ],
    embed:
      '<iframe style="display:block;" class="media youtube" src="https://www.dailymotion.com/embed/video/__id__" allowfullscreen></iframe>',
    thumbnail: "http://www.dailymotion.com/thumbnail/video/__id__",
  },
  vkontakte: {
    reg: [
      /^https?:\/\/vk\.com\/video_ext\.php\?.+$/i,
      /^https?:\/\/www\.vk\.com\/video_ext\.php\?.+$/i,
    ],
    embed:
      '<iframe style="display:block;" class="media youtube" src="__id__" allowfullscreen></iframe>',
  },
  weibo: {
    reg: [
      /^https?:\/\/video\.weibo\.com\/player\/([\w-]+\:[\w-]+)\/v\.swf.*$/i,
    ],
    embed: '<embed src="https://video.weibo.com/player/__id__/v.swf" />',
  },
  youku: {
    reg: [
      /^https?:\/\/player\.youku\.com\/player\.php\/sid\/([=\w-]+\/partnerid\/[\w-]+)\/v\.swf.*$/i,
      /^https?:\/\/(?:www\.)?v\.youku\.com\/v_show\/id_([=\w-]+).*\.html.*$/i,
    ],
    embed:
      '<iframe src="https://player.youku.com/embed/__id__" frameborder=0 allowfullscreen></iframe>',
  },
  aixifan: {
    reg: [
      /^https?:\/\/cdn\.aixifan\.com\/player\/ACFlashPlayer\.out\.swf\?(?:[^&]*&)*vid=([\w-]+).*$/i,
    ],
    embed:
      '<embed src="http://cdn.aixifan.com/player/ACFlashPlayer.out.swf?vid=__id__" />',
  },
  twitch: {
    reg: [/^https?:\/\/(?:www\.)?twitch\.tv\/videos\/([A-Za-z0-9]+).*$/i],
    embed:
      '<iframe style="display:block;" src="https://player.twitch.tv/?video=__id__&parent=__origin__&autoplay=false" allowfullscreen></iframe>',
  },
  tiktok: {
    reg: [/^https?:\/\/(?:www\.)?aweme\.snssdk.*$/i],
    embed: [
      '<video controls="controls" autoplay poster="__thumbnail__">',
      '<source src="/1.0/social/tiktok/video-link?url=__url__" type="video/mp4" />',
      "</video>",
    ].join(""),
  },
};

const INSTAGRAM_HASTAGS_URL = "https://instagram.com/explore/tags/$1/";

const INSTAGRAM_USERNAME_URL = "https://instagram.com/$1";

const FACEBOOK_HASTAGS_URL = "https://www.facebook.com/hashtag/$1";

const GPLUS_HASTAGS_URL = "https://plus.google.com/s/%23$1/top";

const TIKTOK_USERNAME_URL = "https://www.tiktok.com/@$1";

const str = (input: any) => "" + (input || "");

const autoTwitterLink = (status: string) =>
  TwitterText.autoLink(status, {
    // @ts-ignore
    target: "_blank",
    rel: "nofollow noopener",
    suppressNoFollow: true, // Prevent "rel" to be overriden by "nofollow"
    usernameIncludeSymbol: true,
  });

const autoLink = (status: string) => {
  const replacePattern =
    /(\b(https?):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/gim;
  return str(status).replace(
    replacePattern,
    '<a href="$1" target="_blank" rel="noopener">$1</a>'
  );
};

const replaceUserName = (url: string) => (status: string) => {
  return str(status).replace(
    /\B@([(\w-|\.)]+)/gm,
    '<a target="_blank" rel="noopener" href="' + url + '">@$1</a>'
  );
};

// [^&] to prevent html entities to be parsed as hashtags
const replaceHashtags = (url: string) => (status: string) => {
  return str(status).replace(
    /[^&]\B#([(\A-zÀ-ú|\.)]+)/gm,
    ' <a target="_blank" rel="noopener" href="' + url + '">#$1</a>'
  );
};

const parseSinaweiboForward = (status: string) =>
  status.substr(0, status.lastIndexOf("//@"));

const PARSE_PLATFORM_FETCHER = {
  twitter: autoTwitterLink,
  instagram: flowRight(
    replaceHashtags(INSTAGRAM_HASTAGS_URL),
    replaceUserName(INSTAGRAM_USERNAME_URL),
    autoLink
  ),
  tiktok: flowRight(replaceUserName(TIKTOK_USERNAME_URL), autoLink),
  facebook: flowRight(replaceHashtags(FACEBOOK_HASTAGS_URL), autoLink),
  gplus: flowRight(replaceHashtags(GPLUS_HASTAGS_URL), autoLink),
  sinaweib_forward: flowRight(parseSinaweiboForward, autoLink),
};

const getFormatedSocialContentFromPlatform = (
  status: string,
  platform: Platform
) => {
  return get(PARSE_PLATFORM_FETCHER, platform, autoLink)(status);
};

const replaceValues = (content = "", values: object) => {
  return reduce(
    values,
    (acc, value, key) => {
      const regExp = new RegExp("__" + key + "__", "gi");
      return acc.replace(regExp, value || "");
    },
    content
  );
};

const getVideoContent = (
  url = "",
  type: VideoContentType,
  thumbnail: string = "",
  origin: string = ""
) => {
  if (url.indexOf("http:") !== -1) {
    url = url.replace("http:", "https:");
  }
  return reduce(
    VIDEOS_FETCHER,
    (acc, entry) => {
      (entry.reg || []).some((r) => {
        const temp = url.match(r);
        if (temp) {
          acc = replaceValues(entry[type], {
            id: temp.length > 1 ? temp[1] : url,
            url,
            thumbnail,
            origin,
          });
          return true;
        } else {
          return false;
        }
      });
      return acc;
    },
    ""
  );
};

export const parseStatus = (
  status: string,
  platform: Platform = "twitter",
  limit: number = 280
) =>
  parse(
    getFormatedSocialContentFromPlatform(
      limit && limit > 0 ? truncate(status, { length: limit }) : status,
      platform
    )
  );

export function getVideoThumbnail(url: string, thumbnail?: string) {
  return thumbnail || getVideoContent(url, "thumbnail", thumbnail);
}

export function getVideoEmbeddedCode(
  url: string,
  thumbnail?: string,
  origin?: string
) {
  return getVideoContent(url, "embed", thumbnail, origin);
}

const getLabelFromUrl = (url = "") =>
  url.replace(/^(?:https?:\/\/)?(?:www\.)?/i, "");

export const formatWebsite = (website: string) => {
  const match = website.match(join(keys(ACCOUNTS_FETCHER), "|"));
  // @TODO FIX
  return {
    platform: match ? ACCOUNTS_FETCHER[match[0]].name : Platform.Website,
    label: match
      ? website.match(
          // @ts-ignore
          ACCOUNTS_FETCHER[match[0]].reg.find((r) => website.match(r))
        )?.[1] || getLabelFromUrl(website)
      : getLabelFromUrl(website),
    url: website,
    screenName: getLabelFromUrl(website),
  };
};
